import type { ReactNode } from 'react'
import cx from 'classnames'
import { RawButton } from '@/components/Button'
import {
  Dropdown,
  DropdownItem,
  DropdownItemDescription,
  DropdownItemTitle,
} from '@/components/Dropdown'
import { Icon } from '@/components/Icon'
import { TypeBadge } from '@/features/legends/components/TypeBadge'
import { type RynoType, Types } from '@/features/legends/editor/ryno-types'
import styles from './TypeSelector.module.css'

interface TypeOption {
  id: string
  type: RynoType
  title: string
  description: ReactNode
}

const typeOptions: TypeOption[] = [
  {
    id: 'number',
    type: Types.number,
    title: 'Number',
    description: (
      <>
        A numeric value, including negative numbers and fractions. The default
        value is <code>0</code>.
      </>
    ),
  },
  {
    id: 'string',
    type: Types.string,
    title: 'String',
    description: (
      <>
        A piece of text. The default value is an empty string (<code>""</code>{' '}
        in formulas).
      </>
    ),
  },
  {
    id: 'boolean',
    type: Types.boolean,
    title: 'Boolean',
    description: (
      <>
        Holds one of two possible values: <code>true</code> or{' '}
        <code>false</code>.
      </>
    ),
  },
  {
    id: 'number-list',
    type: Types.list(Types.number),
    title: 'Number list',
    description: 'Holds a list of numbers.',
  },
  {
    id: 'string-list',
    type: Types.list(Types.string),
    title: 'String list',
    description: 'Holds a list of strings.',
  },
  {
    id: 'boolean-list',
    type: Types.list(Types.boolean),
    title: 'Boolean list',
    description: 'Holds a list of booleans.',
  },
]

export interface TypeSelectorProps {
  value: RynoType | null
  onChange: (type: RynoType) => unknown
  isDisabled?: boolean
  testId?: string
}

/**
 * Picks a variable's type, describing each one.
 */
export function TypeSelector({
  value,
  onChange,
  isDisabled,
  testId,
}: TypeSelectorProps) {
  return (
    <Dropdown
      autoDismiss
      position="bottom-start"
      isDisabled={isDisabled}
      testId={testId}
      button={
        <RawButton
          className={cx(styles.root, isDisabled && styles.disabled)}
          isDisabled={isDisabled}
        >
          <span className={styles.value}>
            {value ? (
              <TypeBadge type={value} />
            ) : (
              <span className={styles.placeholder}>Please select a type</span>
            )}
          </span>
          <Icon name="arrow" rotate={90} size={16} aria-hidden="true" />
        </RawButton>
      }
    >
      {typeOptions.map((option) => (
        <DropdownItem
          key={option.id}
          active={Types.equalsType(option.type, value)}
          onPress={() => onChange(option.type)}
          testId={testId && `${testId}-${option.id}`}
        >
          <div className={styles.option}>
            <div className={styles.optionText}>
              <DropdownItemTitle>{option.title}</DropdownItemTitle>
              <DropdownItemDescription>
                {option.description}
              </DropdownItemDescription>
            </div>
            <TypeBadge type={option.type} />
          </div>
        </DropdownItem>
      ))}
    </Dropdown>
  )
}
